import { useEffect, useState, useMemo, useCallback } from "react";
import {
  CheckCircle2,
  Clock,
  Target,
  XCircle,
  Hammer,
  Kanban as KanbanIcon,
  GanttChartSquare,
  GitCommit,
  GitPullRequest,
  GitBranch,
  Rocket,
  TestTube,
  Loader2,
  ExternalLink,
  RefreshCw,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import type { ServiceOrderDto } from "@shared/types/ServiceOrderDto";
import type { WorkOrderTicketDto } from "@shared/types/WorkOrderTicketDto";
import type {
  WorkOrderDto,
  WorkUnitDto,
  GithubCommit,
  GithubPullRequest,
  DeploymentDto,
  TestDto,
} from "@shared/types/WorkOrderDto";
import { workOrdersService } from "@/services";

const POLL_INTERVAL = 20_000;

interface ProgressSectionProps {
  serviceOrder: ServiceOrderDto;
}

interface EnrichedTicket extends WorkOrderTicketDto {
  commits: GithubCommit[];
  pullRequests: GithubPullRequest[];
}

function aggregateWorkUnits(workUnits: WorkUnitDto[]) {
  return {
    commits: workUnits.flatMap((wu) => wu.commits),
    pullRequests: workUnits.flatMap((wu) => wu.pullRequests),
    deployments: workUnits.flatMap((wu) => wu.deployments),
    tests: workUnits.flatMap((wu) => wu.tests),
    branches: [
      ...new Set(workUnits.flatMap((wu) => wu.commits.map((c) => c.branch))),
    ],
  };
}

function WorkOrderOverview({ workOrder }: { workOrder: WorkOrderDto }) {
  const agg = aggregateWorkUnits(workOrder.workUnits);
  const openPRs = agg.pullRequests.filter((p) => p.mergeStatus === "OPEN");
  const mergedPRs = agg.pullRequests.filter((p) => p.mergeStatus === "MERGED");
  const passedDeploys = agg.deployments.filter((d) => d.state === "PASSED");
  const buildingDeploys = agg.deployments.filter(
    (d) => d.state === "BUILDING",
  );
  const failedTests = agg.tests.filter((t) => t.state === "FAILED");

  return (
    <Card className="glass-card border-gradient">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <GitBranch className="h-5 w-5 text-primary" />
            Work Overview
          </CardTitle>
          <Badge
            variant={workOrder.status === "in-progress" ? "default" : "secondary"}
          >
            {workOrder.status}
          </Badge>
        </div>
        <p className="text-sm text-muted-foreground">
          {workOrder.workUnits.length} work unit{workOrder.workUnits.length !== 1 ? "s" : ""}
        </p>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          <StatBlock
            icon={<GitCommit className="h-4 w-4" />}
            label="Commits"
            value={agg.commits.length}
          />
          <StatBlock
            icon={<GitPullRequest className="h-4 w-4" />}
            label="PRs"
            value={agg.pullRequests.length}
            sub={`${openPRs.length} open, ${mergedPRs.length} merged`}
          />
          <StatBlock
            icon={<Rocket className="h-4 w-4" />}
            label="Deployments"
            value={agg.deployments.length}
            sub={`${passedDeploys.length} passed${buildingDeploys.length ? `, ${buildingDeploys.length} building` : ""}`}
          />
          <StatBlock
            icon={<TestTube className="h-4 w-4" />}
            label="Tests"
            value={agg.tests.length}
            sub={failedTests.length > 0 ? `${failedTests.length} failed` : "all passing"}
            alert={failedTests.length > 0}
          />
          <StatBlock
            icon={<GitBranch className="h-4 w-4" />}
            label="Branches"
            value={agg.branches.length}
          />
        </div>
      </CardContent>
    </Card>
  );
}

function StatBlock({
  icon,
  label,
  value,
  sub,
  alert,
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
  sub?: string;
  alert?: boolean;
}) {
  return (
    <div className="p-3 rounded-lg bg-card/50 border border-border/50">
      <div className="flex items-center gap-2 text-muted-foreground mb-1">
        {icon}
        <span className="text-xs font-medium">{label}</span>
      </div>
      <p className="text-2xl font-bold">{value}</p>
      {sub && (
        <p className={cn("text-xs mt-1", alert ? "text-red-400" : "text-muted-foreground")}>
          {sub}
        </p>
      )}
    </div>
  );
}

function TicketDetailDialog({
  ticket,
  workOrder,
  open,
  onOpenChange,
}: {
  ticket: EnrichedTicket | null;
  workOrder: WorkOrderDto | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  if (!ticket || !workOrder) return null;

  const agg = aggregateWorkUnits(workOrder.workUnits);
  const ticketCommits = agg.commits.filter(
    (c) => c.ticketId === ticket.ticketId,
  );
  const ticketPRs = agg.pullRequests.filter(
    (pr) => pr.ticketId === ticket.ticketId,
  );
  const ticketDeployments = agg.deployments.filter(
    (d) => d.ticketId === ticket.ticketId,
  );
  const ticketTests = agg.tests.filter(
    (t) => t.ticketId === ticket.ticketId,
  );
  const ticketBranches = [
    ...new Set(ticketCommits.map((c) => c.branch)),
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Badge variant="outline" className="text-xs font-mono">
              {ticket.ticketId}
            </Badge>
            {ticket.title}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 mt-2">
          <div>
            <p className="text-sm text-muted-foreground">{ticket.body}</p>
            <div className="flex items-center gap-3 mt-3 text-xs text-muted-foreground">
              <Badge variant="secondary">{ticket.effort}</Badge>
              <Badge variant={ticket.status === "finished" ? "default" : "outline"}>
                {ticket.status}
              </Badge>
              {ticket.assignee && <span>Assigned: {ticket.assignee}</span>}
            </div>
          </div>

          {ticketBranches.length > 0 && (
            <Section title="Branches" icon={<GitBranch className="h-4 w-4" />} count={ticketBranches.length}>
              <div className="flex flex-wrap gap-2">
                {ticketBranches.map((branch) => (
                  <Badge key={branch} variant="outline" className="font-mono text-xs">
                    {branch}
                  </Badge>
                ))}
              </div>
            </Section>
          )}

          <Section title="Commits" icon={<GitCommit className="h-4 w-4" />} count={ticketCommits.length}>
            {ticketCommits.length > 0 ? (
              <div className="space-y-2">
                {ticketCommits.map((commit) => (
                  <CommitRow key={commit.shasum} commit={commit} />
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No commits</p>
            )}
          </Section>

          <Section title="Pull Requests" icon={<GitPullRequest className="h-4 w-4" />} count={ticketPRs.length}>
            {ticketPRs.length > 0 ? (
              <div className="space-y-2">
                {ticketPRs.map((pr, i) => (
                  <PRRow key={i} pr={pr} />
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No pull requests</p>
            )}
          </Section>

          <Section title="Deployments" icon={<Rocket className="h-4 w-4" />} count={ticketDeployments.length}>
            {ticketDeployments.length > 0 ? (
              <div className="space-y-2">
                {ticketDeployments.map((dep, i) => (
                  <DeploymentRow key={i} deployment={dep} />
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No deployments</p>
            )}
          </Section>

          <Section title="Tests" icon={<TestTube className="h-4 w-4" />} count={ticketTests.length}>
            {ticketTests.length > 0 ? (
              <div className="space-y-2">
                {ticketTests.map((test, i) => (
                  <TestRow key={i} test={test} />
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No tests</p>
            )}
          </Section>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Section({
  title,
  icon,
  count,
  children,
}: {
  title: string;
  icon: React.ReactNode;
  count: number;
  children: React.ReactNode;
}) {
  return (
    <div>
      <h3 className="text-sm font-semibold mb-2 flex items-center gap-2">
        {icon} {title} ({count})
      </h3>
      {children}
    </div>
  );
}

function CommitRow({ commit }: { commit: GithubCommit }) {
  return (
    <div className="p-3 rounded-lg bg-muted/30 border text-sm">
      <div className="flex items-start justify-between gap-2">
        <p className="font-medium">{commit.commitMessage}</p>
        <a href={commit.link} target="_blank" rel="noreferrer" className="shrink-0 text-blue-400 hover:text-blue-300">
          <ExternalLink className="h-3.5 w-3.5" />
        </a>
      </div>
      <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
        <span className="font-mono px-1.5 py-0.5 bg-blue-500/10 text-blue-400 rounded">
          {commit.shasum.slice(0, 7)}
        </span>
        <Badge variant="outline" className="text-xs font-mono">
          {commit.branch}
        </Badge>
        <span>{new Date(commit.createdAt).toLocaleDateString()}</span>
      </div>
    </div>
  );
}

function PRRow({ pr }: { pr: GithubPullRequest }) {
  const statusColor =
    pr.mergeStatus === "MERGED"
      ? "text-purple-400"
      : pr.mergeStatus === "OPEN"
        ? "text-green-400"
        : "text-red-400";

  return (
    <div className="p-3 rounded-lg bg-muted/30 border text-sm">
      <div className="flex items-start justify-between gap-2">
        <p className="font-medium">{pr.title}</p>
        <a href={pr.link} target="_blank" rel="noreferrer" className="shrink-0 text-blue-400 hover:text-blue-300">
          <ExternalLink className="h-3.5 w-3.5" />
        </a>
      </div>
      <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
        {pr.description}
      </p>
      <div className="flex items-center gap-2 mt-2 text-xs">
        <Badge variant="outline" className={cn("text-xs", statusColor)}>
          {pr.mergeStatus}
        </Badge>
        <span className="text-muted-foreground font-mono">
          {pr.originBranch} → {pr.targetBranch}
        </span>
        <span className="text-muted-foreground ml-auto">
          {new Date(pr.createdAt).toLocaleDateString()}
        </span>
      </div>
    </div>
  );
}

function DeploymentRow({ deployment }: { deployment: DeploymentDto }) {
  const stateColor =
    deployment.state === "PASSED"
      ? "text-green-400"
      : deployment.state === "BUILDING"
        ? "text-yellow-400"
        : "text-red-400";

  return (
    <div className="p-3 rounded-lg bg-muted/30 border text-sm">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Rocket className={cn("h-4 w-4", stateColor)} />
          <span className="font-medium">{deployment.name}</span>
        </div>
        <Badge variant="outline" className={cn("text-xs", stateColor)}>
          {deployment.state}
        </Badge>
      </div>
      <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
        <span>{deployment.organization}/{deployment.repo}</span>
        <Badge variant="outline" className="text-xs font-mono">{deployment.branch}</Badge>
      </div>
    </div>
  );
}

function TestRow({ test }: { test: TestDto }) {
  const passed = test.state === "PASSED";
  return (
    <div className="p-3 rounded-lg bg-muted/30 border text-sm">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {passed ? (
            <CheckCircle2 className="h-4 w-4 text-green-400" />
          ) : (
            <XCircle className="h-4 w-4 text-red-400" />
          )}
          <div>
            <p className="font-medium">{test.testName}</p>
            <p className="text-xs text-muted-foreground">{test.testDescription}</p>
          </div>
        </div>
        <Badge variant={passed ? "default" : "destructive"} className="text-xs">
          {test.state}
        </Badge>
      </div>
    </div>
  );
}

export function ProgressSection({ serviceOrder }: ProgressSectionProps) {
  const [view, setView] = useState<"kanban" | "gantt">("kanban");
  const [isLoading, setIsLoading] = useState(true);
  const [workOrder, setWorkOrder] = useState<WorkOrderDto | null>(null);
  const [selectedTicket, setSelectedTicket] = useState<EnrichedTicket | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const columns = [
    { id: "scoping", label: "Scoping", icon: Clock, color: "text-slate-400 bg-slate-400/10" },
    { id: "todo", label: "To Do", icon: Target, color: "text-purple-400 bg-purple-400/10" },
    { id: "in_progress", label: "In Progress", icon: Hammer, color: "text-blue-400 bg-blue-400/10" },
    { id: "blocked", label: "Blocked", icon: XCircle, color: "text-red-400 bg-red-400/10" },
    { id: "finished", label: "Finished", icon: CheckCircle2, color: "text-emerald-400 bg-emerald-400/10" },
  ];

  const fetchWorkOrder = useCallback(async () => {
    try {
      const data = await workOrdersService.getWorkOrderByServiceOrder(
        serviceOrder.id,
      );
      setWorkOrder(data);
    } catch (error) {
      console.error("Error fetching work order:", error);
    } finally {
      setIsLoading(false);
    }
  }, [serviceOrder.id]);

  useEffect(() => {
    fetchWorkOrder();
    const interval = setInterval(fetchWorkOrder, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchWorkOrder]);

  const tickets: EnrichedTicket[] = useMemo(() => {
    const allCommits = workOrder?.workUnits.flatMap((wu) => wu.commits) ?? [];
    const allPRs = workOrder?.workUnits.flatMap((wu) => wu.pullRequests) ?? [];

    return serviceOrder.tickets.map((ticket) => {
      const commits = allCommits.filter((c) => c.ticketId === ticket.ticketId);
      const pullRequests = allPRs.filter((pr) => pr.ticketId === ticket.ticketId);
      return { ...ticket, commits, pullRequests };
    });
  }, [serviceOrder.tickets, workOrder]);

  const handleTicketClick = (ticket: EnrichedTicket) => {
    setSelectedTicket(ticket);
    setDialogOpen(true);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {workOrder && <WorkOrderOverview workOrder={workOrder} />}

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 p-1 bg-card/50 rounded-lg w-fit">
          <Button
            variant={view === "kanban" ? "default" : "ghost"}
            size="sm"
            onClick={() => setView("kanban")}
            className="gap-2"
          >
            <KanbanIcon className="h-4 w-4" />
            Kanban
          </Button>
          <Button
            variant={view === "gantt" ? "default" : "ghost"}
            size="sm"
            onClick={() => setView("gantt")}
            className="gap-2"
          >
            <GanttChartSquare className="h-4 w-4" />
            Timeline
          </Button>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={fetchWorkOrder}
          className="gap-2 text-muted-foreground"
        >
          <RefreshCw className="h-3.5 w-3.5" />
          Refresh
        </Button>
      </div>

      {view === "kanban" ? (
        <div className="flex gap-4 overflow-x-auto pb-4">
          {columns.map((col) => {
            const columnTickets = tickets.filter((t) => t.status === col.id);
            return (
              <div key={col.id} className="flex-1 min-w-[250px]">
                <div
                  className={cn(
                    "flex items-center gap-2 mb-4 p-2 rounded-lg",
                    col.color,
                  )}
                >
                  <col.icon className="h-4 w-4" />
                  <span className="font-medium text-sm">{col.label}</span>
                  <span className="ml-auto text-xs opacity-70">
                    {columnTickets.length}
                  </span>
                </div>
                <div className="space-y-3">
                  {columnTickets.map((ticket) => (
                    <Card
                      key={ticket.ticketId}
                      className="glass-card cursor-pointer hover:border-primary/40 transition-colors"
                      onClick={() => handleTicketClick(ticket)}
                    >
                      <CardContent className="p-4">
                        <h4 className="font-medium text-sm mb-1">
                          {ticket.title}
                        </h4>
                        <p className="text-xs text-muted-foreground mb-2">
                          {ticket.ticketId}
                        </p>
                        <p className="text-xs text-muted-foreground mb-3 line-clamp-2">
                          {ticket.body}
                        </p>
                        <div className="flex items-center justify-between">
                          <Badge variant="secondary" className="text-xs">
                            {ticket.effort}
                          </Badge>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            {ticket.commits.length > 0 && (
                              <div className="flex items-center gap-1">
                                <GitCommit className="h-3 w-3" />
                                {ticket.commits.length}
                              </div>
                            )}
                            {ticket.pullRequests.length > 0 && (
                              <div className="flex items-center gap-1">
                                <GitPullRequest className="h-3 w-3" />
                                {ticket.pullRequests.length}
                              </div>
                            )}
                          </div>
                        </div>
                        {ticket.assignee && (
                          <p className="text-xs text-muted-foreground mt-2 pt-2 border-t border-border/30">
                            Assigned: {ticket.assignee}
                          </p>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                  {columnTickets.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground text-sm border border-dashed border-border/50 rounded-lg">
                      No items
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <Card className="glass-card">
          <CardContent className="p-8 text-center text-muted-foreground">
            <GanttChartSquare className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>Timeline view coming soon</p>
          </CardContent>
        </Card>
      )}

      <TicketDetailDialog
        ticket={selectedTicket}
        workOrder={workOrder}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
      />
    </div>
  );
}
